/**
 * Game Service - Handles conversion between frontend and backend data formats
 *
 * Frontend uses: Complex Army structure with Soldier objects (each with ID)
 * Backend uses: Simple count-based Army structure (just numbers)
 *
 * This service provides the translation layer.
 */

import { Player, Army, Soldier, SiegeEquipment, SoldierType, Team, GameState, UnitStats } from '../types';
import api from './api';

// Map between frontend and backend soldier type names
const soldierTypeToBackend: Record<SoldierType, string> = {
  'King': 'king',
  'Knight': 'knight',
  'Archer': 'archer',
  'Crossbow': 'crossbowman',
  'Healer': 'healer',
  'Siege Equipment': 'siegeEquipment'
};

const soldierTypeFromBackend: Record<string, SoldierType> = {
  'king': 'King',
  'knight': 'Knight',
  'archer': 'Archer',
  'crossbowman': 'Crossbow',
  'healer': 'Healer',
  'siegeEquipment': 'Siege Equipment'
};

// Convert frontend Army (with Soldier objects) to backend Army (counts)
export function armyToBackend(army: Army): {
  archers: number;
  crossbowmen: number;
  siegeEquipment: number;
  knights: number;
  healers: number;
  king: number;
} {
  return {
    archers: army.archers.length,
    crossbowmen: army.crossbows.length,
    siegeEquipment: army.siegeEquipment.length,
    knights: army.knights.length,
    healers: army.healers.length,
    king: army.king ? 1 : 0,
  };
}

// Convert backend Army (counts) to frontend Army (with Soldier objects)
export function armyFromBackend(backendArmy: {
  archers: number;
  crossbowmen: number;
  siegeEquipment: number;
  knights: number;
  healers: number;
  king: number;
}): Army {
  const createSoldiers = (count: number, type: SoldierType): Soldier[] => {
    return Array.from({ length: count }, (_, i) => ({
      id: `${type}-${Date.now()}-${i}`,
      type,
    }));
  };

  const createSiegeEquipment = (count: number): SiegeEquipment[] => {
    return Array.from({ length: count }, (_, i) => ({
      id: `Siege Equipment-${Date.now()}-${i}`,
      type: 'Siege Equipment' as const,
      manning: [],
    }));
  };

  return {
    king: backendArmy.king > 0 ? { id: `King-${Date.now()}`, type: 'King' } : null,
    knights: createSoldiers(backendArmy.knights, 'Knight'),
    archers: createSoldiers(backendArmy.archers, 'Archer'),
    crossbows: createSoldiers(backendArmy.crossbowmen, 'Crossbow'),
    healers: createSoldiers(backendArmy.healers, 'Healer'),
    siegeEquipment: createSiegeEquipment(backendArmy.siegeEquipment),
  };
}

// Backend player type
interface BackendPlayer {
  id: string;
  name: string;
  team: 'kingdom' | 'raiders';
  raidPoints: number;
  raidPointsSpent: number;
  army: {
    archers: number;
    crossbowmen: number;
    siegeEquipment: number;
    knights: number;
    healers: number;
    king: number;
  };
  userId?: string;
  createdAt: string;
  updatedAt: string;
}

// Convert backend player to frontend player
export function playerFromBackend(backendPlayer: BackendPlayer): Player {
  return {
    id: backendPlayer.id,
    name: backendPlayer.name,
    team: backendPlayer.team === 'kingdom' ? 'defenders' : 'raiders',
    raidPoints: backendPlayer.raidPoints,
    raidPointsSpent: backendPlayer.raidPointsSpent,
    army: armyFromBackend(backendPlayer.army),
    userId: backendPlayer.userId,
  };
}

// Game Service with caching and sync
export class GameService {
  private syncInProgress = false;

  async getGameState(): Promise<GameState> {
    try {
      const backendState: any = await api.getGameState();
      // Handle null players array from backend (when no players exist)
      const players = backendState.players ? (backendState.players as BackendPlayer[]).map(playerFromBackend) : [];

      // Convert backend unit stats keys to frontend format
      const unitStats: Record<SoldierType, UnitStats> = {} as Record<SoldierType, UnitStats>;
      for (const [backendKey, stats] of Object.entries(backendState.unitStats)) {
        const frontendKey = soldierTypeFromBackend[backendKey];
        if (frontendKey) {
          unitStats[frontendKey] = {
            ...(stats as any),
            type: frontendKey
          };
        }
      }

      return {
        players,
        unitStats,
        battlefieldBackgroundUrl: backendState.battlefieldBackgroundUrl,
        kingRaidersImageUrl: backendState.kingRaidersImageUrl,
        kingDefendersImageUrl: backendState.kingDefendersImageUrl,
      };
    } catch (error) {
      console.error('Failed to fetch game state from backend:', error);
      throw error;
    }
  }

  async createPlayer(name: string, team: Team, userId?: string): Promise<Player> {
    const backendTeam = team === 'defenders' ? 'kingdom' : 'raiders';
    const backendPlayer: any = await api.createPlayer(name, backendTeam as Team, userId);
    return playerFromBackend(backendPlayer as BackendPlayer);
  }

  async updatePlayer(
    playerId: string,
    updates: {
      name?: string;
      team?: Team;
      raidPoints?: number;
      raidPointsSpent?: number;
      army?: Army;
      userId?: string;
    }
  ): Promise<Player> {
    const backendUpdates: any = {};

    if (updates.name !== undefined) {
      backendUpdates.name = updates.name;
    }
    if (updates.team !== undefined) {
      backendUpdates.team = updates.team === 'defenders' ? 'kingdom' : 'raiders';
    }
    if (updates.raidPoints !== undefined) {
      backendUpdates.raidPoints = updates.raidPoints;
    }
    if (updates.raidPointsSpent !== undefined) {
      backendUpdates.raidPointsSpent = updates.raidPointsSpent;
    }
    if (updates.army !== undefined) {
      backendUpdates.army = armyToBackend(updates.army);
    }
    if (updates.userId !== undefined) {
      backendUpdates.userId = updates.userId;
    }

    const backendPlayer: any = await api.updatePlayer(playerId, backendUpdates);
    return playerFromBackend(backendPlayer as BackendPlayer);
  }

  async deletePlayer(playerId: string): Promise<void> {
    await api.deletePlayer(playerId);
  }

  async resetPlayerArmy(playerId: string): Promise<Player> {
    const backendPlayer: any = await api.resetPlayerArmy(playerId);
    return playerFromBackend(backendPlayer as BackendPlayer);
  }

  async getPlayerByUserId(userId: string): Promise<Player | null> {
    const backendPlayer: any = await api.getPlayerByUserId(userId);
    if (!backendPlayer) return null;
    return playerFromBackend(backendPlayer as BackendPlayer);
  }

  async updateUnitStats(
    type: SoldierType,
    updates: {
      hp?: number;
      damage?: number;
      cost?: number;
    }
  ): Promise<void> {
    // Convert frontend soldier type to backend format
    const backendType = soldierTypeToBackend[type];
    await api.updateUnitStats(backendType as SoldierType, updates);
  }

  async resetGame(): Promise<void> {
    await api.resetGame();
  }
}

export const gameService = new GameService();
